import { useEffect, useRef } from "react";
import { io, type Socket } from "socket.io-client";
import { useAdminAuthStore } from "@/stores/adminAuthStore";
import { mocksEnabled } from "@/portals/clinician/api/transport";
import { hardenSocket, onResync } from "@/lib/socketRecovery";

const BASE_URL = import.meta.env.VITE_API_URL ?? "";

// The admin portal's own connection (same pattern as clinicianSocket.ts): `forceNew`
// keeps it on its own Manager, so it never shares a handshake token with the
// patient or clinician sockets. The server joins it to admin:{adminId} and admins.
// Created lazily, and only the admin layout / admin bell ever ask for it — a
// patient or clinician session never creates it.
let socket: Socket | null = null;

export function getAdminSocket(): Socket {
  if (socket) return socket;
  socket = io(BASE_URL || undefined, {
    autoConnect: false,
    forceNew: true,
    withCredentials: true,
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10_000,
  });
  socket.on("connect", () => console.info("[realtime:admin] connected", socket?.id));
  socket.on("disconnect", (reason) => console.warn("[realtime:admin] disconnected:", reason));
  socket.on("connect_error", (err) => console.error("[realtime:admin] failed to connect:", err.message));
  hardenSocket(socket, () => Boolean(useAdminAuthStore.getState().accessToken));
  return socket;
}

/** For tests (M6-F7): the admin socket if one was created, without creating it. */
export function peekAdminSocket(): Socket | null {
  return socket;
}

export function syncAdminSocket(token: string | null): void {
  if (mocksEnabled()) return; // no Socket.IO server behind the mocks
  if (!token && !socket) return; // nothing to disconnect — don't create one just to close it
  const s = getAdminSocket();
  if (!token) {
    s.disconnect();
    return;
  }
  s.auth = { token };
  if (!s.connected) s.connect();
}

/** Mounted by the protected admin layout: connects with the admin token, disconnects on logout/unmount. */
export function useAdminSocket(): void {
  const token = useAdminAuthStore((s) => s.accessToken);
  useEffect(() => {
    syncAdminSocket(token);
  }, [token]);
  useEffect(() => () => syncAdminSocket(null), []);
}

/**
 * Calls `handler` on an admin socket event (e.g. "review_queue:changed", which
 * carries no data — clients just refetch), and after a reconnect / resync,
 * when that event may have been missed. No-op under mocks.
 */
export function useAdminSocketEvent(event: string, handler: () => void): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (mocksEnabled()) return;
    const s = getAdminSocket();
    const on = () => ref.current();
    s.on(event, on);
    const offResync = onResync(s, on);
    return () => {
      s.off(event, on);
      offResync();
    };
  }, [event]);
}
